/**
 * Centralized application state with bidirectional window.* bridge.
 *
 * New code should read/write via `AppState.propertyName`.
 * Legacy page scripts continue to work via `window.propertyName`
 * because the bridge keeps both in sync.
 */
export const AppState = {};
window.AppState = AppState;

function defineWindowBridge(key, initial = undefined) {
	// Seed window[key] so bare-variable references in other modules
	// (which resolve to window.* in non-strict mode) find the property.
	if (!(key in window)) {
		window[key] = initial;
	}
	// window[key] is the single source of truth. Both AppState.foo and
	// bare `foo` references (via window) always read/write the same slot.
	Object.defineProperty(AppState, key, {
		get() {
			return window[key];
		},
		set(v) {
			window[key] = v;
		},
		enumerable: true,
		configurable: true,
	});
}

// ── Core data objects ──────────────────────────────────────────────
defineWindowBridge('userData');
defineWindowBridge('clientData');
defineWindowBridge('capTableData');
defineWindowBridge('shareholderData');

// ── Auth & session ─────────────────────────────────────────────────
defineWindowBridge('cookie');
defineWindowBridge('authenticated');
defineWindowBridge('authType');
defineWindowBridge('authenticatorValue');

// ── User role flags ────────────────────────────────────────────────
defineWindowBridge('isAdmin', false);
defineWindowBridge('isCont', false);
defineWindowBridge('isExec', false);
defineWindowBridge('isSupv', false);

// ── UI / display state ─────────────────────────────────────────────
defineWindowBridge('formattingConfig');
defineWindowBridge('language');
defineWindowBridge('segment');
defineWindowBridge('itsaFund');
defineWindowBridge('showTabs', false);
defineWindowBridge('requestEmail');

// ── Campaign state ─────────────────────────────────────────────────
defineWindowBridge('activeCampaign');
defineWindowBridge('campaignName');
defineWindowBridge('previewMode', false);
defineWindowBridge('campaignTags');
defineWindowBridge('campaignClasses', []);
defineWindowBridge('campaignDocuments', []);
defineWindowBridge('activePaymentsCampaign');
defineWindowBridge('activeRequestsCampaign');

// ── Feature flags (mutable — set by index.js from clientData) ──────
defineWindowBridge('SHOW_SMALL');
defineWindowBridge('HIDE_ZEROS');
defineWindowBridge('INCLUDE_ROC');
defineWindowBridge('LIVE_NOTICES');
defineWindowBridge('USE_TEMPLATES');
defineWindowBridge('USE_TAX_BASIS');
defineWindowBridge('SHOW_TAX_BASIS_COLUMN');
defineWindowBridge('USE_CERTIFICATES');
defineWindowBridge('SURVEYING');
defineWindowBridge('VIEWING');
defineWindowBridge('VOTING');
defineWindowBridge('PAYING');
defineWindowBridge('ENROLL');
defineWindowBridge('BUYSELL');
defineWindowBridge('SIGNING');
defineWindowBridge('FUNDING');
defineWindowBridge('REQUESTS');
defineWindowBridge('HIDE_CTA');
defineWindowBridge('BUYERS_ONLY');
defineWindowBridge('SHOW_INVEST');
defineWindowBridge('LIVE_TRADING');
defineWindowBridge('VOA');
defineWindowBridge('STD_EXT_DATA');
defineWindowBridge('EXTERNAL_DATA');
defineWindowBridge('USE_AUTHENTICATOR', true);
defineWindowBridge('PUBLISH_INV_AGMT', false);
defineWindowBridge('signInAnalyticsCalled', false);

// ── Feature constants (effectively immutable) ──────────────────────
defineWindowBridge('NO_DOCS', false);
defineWindowBridge('USE_PIN', false);
defineWindowBridge('ADDITIONAL_INV', false);
defineWindowBridge('READY_TO_PAY', false);
defineWindowBridge('ALERA_PERMIT_PURCHASE', true);
defineWindowBridge('ALERA_APPROVED_PERCENT', 20);
defineWindowBridge('customPermit', {});

// ── Immutable identifiers (set once by auth.js) ────────────────────
defineWindowBridge('env');
defineWindowBridge('client');
defineWindowBridge('authedUser');
defineWindowBridge('nthAdmin');

// ── Session / refresh state ────────────────────────────────────────
defineWindowBridge('typoError', false);
defineWindowBridge('refreshTime');
defineWindowBridge('classes', []);

// ── Viewport / location ────────────────────────────────────────────
defineWindowBridge('narrow');
defineWindowBridge('hn');
defineWindowBridge('pn');

// ── Debug ──────────────────────────────────────────────────────────
defineWindowBridge('blurBlock');
defineWindowBridge('blurNames');
